import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { LivenessBadge, errMsg, fmtDateTime, useSecretsAccess } from './common';

// Shared panel for a key's liveness deep analysis. Used inside the modal on the
// Keys list and on the Key detail page. Two steps: analyse, then (after a
// person confirms) run the probe. Raw values never leave the code box.

function Collapsible({ label, children, open: open0 = false }) {
  const [open, setOpen] = useState(open0);
  return (
    <div style={{ marginTop: 8 }}>
      <button type="button" className="btn btn-ghost" style={{ padding: '2px 0', fontSize: 12 }}
        onClick={() => setOpen(!open)}>{open ? '▾' : '▸'} {label}</button>
      {open && children}
    </div>
  );
}

function Pre({ children }) {
  return (
    <pre style={{ background: 'var(--bg-base)', border: '1px solid var(--border)', borderRadius: 6,
      padding: 10, fontSize: 11, maxHeight: 320, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {children}
    </pre>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ display: 'flex', gap: 10, fontSize: 13, margin: '4px 0' }}>
      <div style={{ width: 130, color: 'var(--text-muted)', flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

export function KeyAnalysisPanel({ keyId, onChanged }) {
  const { can_edit: canEdit } = useSecretsAccess();
  const [d, setD] = useState(undefined); // undefined = loading, null = none
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const timer = useRef(null);

  const load = useCallback((announce) => {
    axios.get(`/secrets/keys/${keyId}/analysis`)
      .then((r) => {
        setD(r.data.latest || null);
        if (announce && r.data.latest) onChanged?.(r.data.latest);
      })
      .catch((e) => setMsg(errMsg(e)));
  }, [keyId, onChanged]);

  useEffect(() => { load(false); return () => clearTimeout(timer.current); }, [load]);

  // Poll while a step is running; tell the parent when it settles.
  useEffect(() => {
    const running = d && (d.status === 'analyzing' || d.status === 'probing');
    if (!running) { onChanged?.(d); return undefined; }
    timer.current = setTimeout(() => load(true), 3000);
    return () => clearTimeout(timer.current);
  }, [d, load, onChanged]);

  const post = (url, okMsg) => {
    setBusy(true); setMsg('');
    axios.post(url)
      .then(() => { setMsg(okMsg); load(true); })
      .catch((e) => setMsg(errMsg(e)))
      .finally(() => setBusy(false));
  };
  const analyze = () => post(`/secrets/keys/${keyId}/analysis`, 'Analysis started. It reads the key’s usage on the code box.');
  const probe = () => {
    const warn = d.probe_is_write
      ? '\n\nThis probe causes a benign side effect (see the plan). Run it against the live resource?'
      : '\n\nRun this read-only probe against the live resource now?';
    if (!window.confirm((d.probe_summary || 'Run the liveness probe?') + warn)) return;
    post(`/secrets/keys/${keyId}/analysis/${d.id}/probe`, 'Probe started. Using the real credential on the code box…');
  };

  if (d === undefined) return <div style={{ padding: 12 }}><span className="spinner" /> Loading analysis…</div>;

  const status = d?.status;
  const running = status === 'analyzing' || status === 'probing';

  return (
    <div style={{ fontSize: 13 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <LivenessBadge liveness={d ? (d.liveness === 'unknown' && d.status !== 'done' ? d.status : d.liveness) : 'unknown'} />
        {d?.liveness_at_label}
        {d && <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
          {status === 'awaiting_probe' ? 'analysis done, probe not yet run'
            : status === 'done' ? `probed ${d.probed_at ? fmtDateTime(d.probed_at) : ''}`
              : running ? (status === 'analyzing' ? 'reading usage on the code box…' : 'probing the live resource…')
                : status === 'error' ? 'failed' : ''}
        </span>}
        <span style={{ flex: 1 }} />
        {canEdit && !running && (
          <button className="btn btn-secondary" disabled={busy} onClick={analyze}>
            {d ? 'Re-analyse' : 'Start deep analysis'}
          </button>
        )}
      </div>

      {!d && <div style={{ color: 'var(--text-muted)' }}>
        No analysis yet. A deep analysis reads how this key is used on the code box, identifies the resource it
        guards, and prepares a non-destructive probe. Nothing touches the live resource until you confirm.
      </div>}

      {d?.error && <div className="error-msg" style={{ marginBottom: 8 }}>{d.error}</div>}

      {d && (status === 'awaiting_probe' || status === 'done' || (status === 'error' && d.resource_kind)) && (
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8 }}>
          <Row label="Resource">{d.resource_kind || '—'}</Row>
          {d.resource_detail && <Row label="Detail">{d.resource_detail}</Row>}
          {d.confidence && <Row label="Confidence">{d.confidence}</Row>}
          {d.analysis_json?.companions?.length > 0 && (
            <Row label="Also reads">
              {d.analysis_json.companions.map((comp, i) => (
                <div key={i} style={{ fontSize: 12 }}>{comp.env_name}
                  <span style={{ color: 'var(--text-muted)' }}> from {comp.repo_slug ? `${comp.repo_slug}:` : ''}{comp.file_path}</span></div>
              ))}
              <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 2 }}>
                Paired secrets fetched on the box at probe time; their values never leave it.
              </div>
            </Row>
          )}
          {status === 'done' && <>
            <Row label="Reachable">{d.reachable === 'yes' ? 'Yes' : d.reachable === 'no' ? 'No' : 'Unknown'}</Row>
            <Row label="Verdict"><LivenessBadge liveness={d.liveness} /> {d.verdict_detail}</Row>
          </>}
          {status !== 'done' && d.probe_summary && (
            <Row label="Probe plan">
              {d.probe_summary}
              {d.probe_is_write
                ? <div style={{ color: '#fbbf24', marginTop: 4 }}>⚠ This probe causes a benign side effect.</div>
                : <div style={{ color: '#86efac', marginTop: 4 }}>Read-only: no change to the resource.</div>}
            </Row>
          )}
          {status === 'awaiting_probe' && canEdit && (
            <div style={{ marginTop: 10 }}>
              <button className="btn btn-primary" disabled={busy} onClick={probe}>
                {d.probe_is_write ? 'Run probe (causes a benign write)' : 'Run probe (read-only)'}
              </button>
            </div>
          )}
          {d.probe_output && <Collapsible label="Probe output (secret scrubbed)"><Pre>{d.probe_output}</Pre></Collapsible>}
          {d.probe_script && <Collapsible label={`Probe script (${d.probe_language || 'bash'}, reads $PF_SECRET)`}><Pre>{d.probe_script}</Pre></Collapsible>}
          {d.analysis_json?.notes && <Collapsible label="Analyst notes"><Pre>{d.analysis_json.notes}</Pre></Collapsible>}
        </div>
      )}

      {running && <div style={{ marginTop: 8, color: 'var(--text-secondary)' }}><span className="spinner" /> This runs in the background; you can close this and come back.</div>}
      {msg && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-secondary)' }}>{msg}</div>}
    </div>
  );
}

// Modal wrapper for use from the Keys list.
export function KeyAnalysisModal({ keyId, redacted, onClose, onChanged }) {
  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '6vh 16px', overflow: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()} className="card"
        style={{ width: 720, maxWidth: '100%', maxHeight: '86vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ fontSize: 15 }}>Deep analysis</h3>
          {redacted && <code style={{ fontSize: 12, marginLeft: 10, color: 'var(--text-muted)' }}>{redacted}</code>}
          <span style={{ flex: 1 }} />
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
        <KeyAnalysisPanel keyId={keyId} onChanged={onChanged} />
      </div>
    </div>
  );
}
